import { MouseEvent, useEffect, useRef, useState } from 'react'
import classnames from 'classnames/bind'
import { format } from 'date-fns'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import qs from 'qs'

// Libs
import { envConfigs, ENVIORNMENT } from '@/constants/config'
import { getLiveRoomPath, goToLiveRoom } from '@/methods/goToLiveRoom'
import { handleGtmBookCalendarLive, handleGtmClickLiveRoom } from '@/methods/handleGtmEvent'
import handleImageBlur from '@/methods/handleImageBlur'
import useI18n from '@/hooks/useI18n'
import useLiveImpression from '@/hooks/useLiveImpression'
import { LIVE_TRACK_EVENT, liveSourceFromPathname, trackLiveEvent } from '@/methods/liveTracking'

// Components
import Image, { PlaceholderType } from '@/components/common/Image'

// Constants
import { DEFAULT_LOCALE } from '@/constants/defaultLocale'
import { GTM_LOCATION, GtmClickLiveRoomLiveStatus, GtmClickLiveRoomPosition } from '@/constants/gtmEvent'
import { ImageSizes } from '@/constants/imageSizes'
import { ROOM_LIVE_TYPE } from '@/constants/liveRoom'
import { ROUTE_PATH } from '@/constants/routePath'
import { SENTRY_TAGS } from '@/constants/sentry'

// Methods
import { getCompanyBrandName } from '@/components/pages/Terms/methods/company'

// Types
import type { LiveListType } from '@/components/shared/LiveList/hooks/useLiveList'

// Styles
import styles from './style.module.scss'

const cx = classnames.bind(styles)

/** google calendar 要 20210831T100000 這種格式（同 LiveItem） */
const formatCalendarTime = (time: string) => time.replace(/-/g, '').replace(/:/g, '').replace('+0800', '')

/**
 * 首頁「直播購物」卡片（Figma 首頁V2 Live screen-new）。
 * 直播中：左上 LIVE + 觀看數，按鈕「進入直播間」；預告：左上 預告 + 開播時間，按鈕「預約直播」（加到 Google 日曆）。
 */
const LiveShoppingCard = (room: LiveListType) => {
  const { roomId, title, coverPhotoUrl, kolHeadshotUrl, kolName, startTime, endTime, dummyViews, liveType, liveSourceType, roomMode } = room

  const { t } = useTranslation()
  const router = useRouter()
  const { isOverseaUser } = useI18n()

  const isLive = liveType === ROOM_LIVE_TYPE.CURRENT
  const isFuture = liveType === ROOM_LIVE_TYPE.FUTURE
  const liveRoomPath = getLiveRoomPath({ roomId, roomMode })
  const cardRef = useRef<HTMLDivElement>(null)

  useLiveImpression({ ref: cardRef, type: LIVE_TRACK_EVENT.COVER_IMPRESSION, roomId, source: liveSourceFromPathname(router.pathname) })

  // 日期在 client 端才格式化，避免 SSR 時區不同造成 hydration 落差（同 LiveItem）
  const [startTimeText, setStartTimeText] = useState('')

  useEffect(() => {
    setStartTimeText(startTime ? format(new Date(startTime), 'MM-dd HH:mm') : '')
  }, [startTime])

  const handleEnterRoom = () => {
    goToLiveRoom({ router, roomId, liveType, liveSourceType, roomMode })
    handleGtmClickLiveRoom({
      position: GtmClickLiveRoomPosition.ROOM_CARD,
      roomId,
      liveStatus: isLive ? GtmClickLiveRoomLiveStatus.LIVE : isFuture ? GtmClickLiveRoomLiveStatus.PREVIEW : GtmClickLiveRoomLiveStatus.REVIEW,
    })
  }

  // 預約流程照 LiveItem：stage / pre / prod 的日曆連結先經過 analysis 頁追蹤
  const handleReserve = (event: MouseEvent) => {
    event.stopPropagation()
    trackLiveEvent({ type: LIVE_TRACK_EVENT.RESERVE, roomId, source: liveSourceFromPathname(router.pathname) })

    const currentLocale = router.locale === 'default' ? DEFAULT_LOCALE : router.locale
    const liveUrl = `${envConfigs.domainUrl}${liveRoomPath}`
    const analysisUrl = `${envConfigs.domainUrl}/${currentLocale}/${ROUTE_PATH.ANALYSIS}`
    const analysisUrlWithQs = `${analysisUrl}?${qs.stringify({ [SENTRY_TAGS.RESERVATION_LIVE_ROOM]: liveUrl })}`
    const isTrackedEnv = [ENVIORNMENT.STAGE, ENVIORNMENT.PRE, ENVIORNMENT.PROD].includes(process.env.DEPLOY_ENV as string)

    handleGtmBookCalendarLive({ location: GTM_LOCATION.INDEX, roomId })

    const calendarTitle = t('Live_Calandar_Title', { kolName, title, brandName: getCompanyBrandName(isOverseaUser) })
    const description = t('Live_Calandar_Description', {
      displayDateWithTime: startTimeText,
      liveUrl: isTrackedEnv ? analysisUrlWithQs : liveUrl,
    })

    window.open(
      `https://calendar.google.com/calendar/render?action=TEMPLATE&dates=${formatCalendarTime(startTime)}/${formatCalendarTime(
        endTime,
      )}&details=${description}&text=${calendarTitle}`,
    )
  }

  return (
    <div className={cx('card')} onClick={handleEnterRoom} ref={cardRef}>
      <div className={cx('card__cover')}>
        <Image
          src={coverPhotoUrl}
          fill
          style={{ objectFit: 'cover', objectPosition: 'center' }}
          alt={title}
          sizes='(max-width: 710px) 50vw, 260px'
          placeholder={PlaceholderType.blur}
          blurDataURL={handleImageBlur()}
        />

        {(isLive || isFuture) && (
          <div className={cx('status')}>
            {isLive ? (
              <>
                <span className={cx('status-tag', 'status-tag--live')}>
                  LIVE
                  <Image src='/images/svg/home-live/signal.svg' width={18} height={18} alt='' />
                </span>
                <span className={cx('status-text')}>{t('Common_Views', { views: dummyViews })}</span>
              </>
            ) : (
              <>
                <span className={cx('status-tag', 'status-tag--future')}>{t('Live_Future')}</span>
                <span className={cx('status-text')}>{startTimeText}</span>
              </>
            )}
          </div>
        )}
      </div>

      <div className={cx('card__info')}>
        <span className={cx('card__avatar')}>
          <Image src={kolHeadshotUrl} fill style={{ objectFit: 'cover' }} alt={kolName} sizes={ImageSizes.headShot} />
        </span>

        <div className={cx('card__text')}>
          <p className={cx('card__name')}>{kolName}</p>
          <h3 className={cx('card__title')}>{title}</h3>
        </div>

        {isFuture ? (
          <button type='button' className={cx('card__button', 'card__button--outline')} onClick={handleReserve}>
            {t('Live_Reserve_Button')}
          </button>
        ) : (
          <button type='button' className={cx('card__button')}>
            {t('Home_Live_Shopping_Enter')}
          </button>
        )}
      </div>
    </div>
  )
}

export default LiveShoppingCard
